import React, { useMemo } from "react";
import type { EChartsOption } from "echarts";
import type { CallbackDataParams, TopLevelFormatterParams } from "echarts/types/dist/shared";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import { MetricKeys } from "@/constants/metrics";
import { useQuery } from "@tanstack/react-query";
import { fetchTokensChanges, fetchTokensRollups } from "@/services/queries/tokens/tokensScreenerQueries";
import { useMetricsCatalog } from "@/hooks/useMetricsCatalog";
import type { MetricInfo } from "@/types/metrics";

// Stablecoin filtering now handled via category map upstream

type Props = {
  rows: Array<Record<string, unknown>>;
  range: '1d' | '7d' | '30d' | '365d';
  xKey: string;
  yKey: string;
  xUseChange: boolean;
  yUseChange: boolean;
  logX: boolean;
  logY: boolean;
  setLogAvailability?: (avail: { x: boolean; y: boolean }) => void;
  search: string;
  mcMin: number | '';
  mcMax: number | '';
  height?: number | string;
  labelFromCatalog?: (key: string) => string;
};

const ScatterChart: React.FC<Props> = ({ rows, range, xKey, yKey, xUseChange, yUseChange, logX, logY, setLogAvailability, search, mcMin, mcMax, height = 640, labelFromCatalog: labelFromCatalogProp }) => {
  const baseGrid = useMemo(() => ({ left: 10, right: 10, top: 10, bottom: 10, containLabel: true } as const), []);
  const baseTextStyle = useMemo(() => ({ color: '#e2e8f0' } as const), []);
  const axisCommon = useMemo(() => ({ axisLabel: { color: '#94a3b8', fontSize: 10 }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { show: true, lineStyle: { color: '#334155', opacity: 0.35 } } } as const), []);

  // Catalog and helpers
  const { data: catalog } = useMetricsCatalog();
  const catalogMap: Record<string, MetricInfo> = useMemo(() => {
    const map: Record<string, MetricInfo> = {};
    (catalog || []).forEach((m) => { if ((m as MetricInfo)?.metric_key) map[m.metric_key] = m as MetricInfo; });
    return map;
  }, [catalog]);
  const labelFromCatalogInternal = useMemo(() => (key: string): string => {
    const entry = catalogMap[key] as unknown as { display_name?: string | null; label?: string | null; name?: string | null } | undefined;
    return (entry?.display_name || entry?.label || entry?.name || key);
  }, [catalogMap]);
  
  // Use provided labelFromCatalog or fall back to internal one
  const labelFromCatalog = labelFromCatalogProp || labelFromCatalogInternal;
  const fmtAxisLabel = useMemo(() => (key: string, useChange: boolean) => {
    if (useChange) return '{value}%';
    const unit = catalogMap[String(key)]?.unit || '';
    if (typeof unit === 'string' && /usd|\$|currency/i.test(unit)) {
      return (v: number) => `$${(Number(v) >= 1e9 ? (v/1e9).toFixed(1)+'B' : Number(v) >= 1e6 ? (v/1e6).toFixed(1)+'M' : Number(v).toFixed(0))}`;
    }
    if (/cap|usd|tvl|revenue|fees|volume/i.test(String(key))) {
      return (v: number) => `$${(Number(v) >= 1e9 ? (v/1e9).toFixed(1)+'B' : Number(v) >= 1e6 ? (v/1e6).toFixed(1)+'M' : Number(v).toFixed(0))}`;
    }
    return undefined;
  }, [catalogMap]);

  // Changes query (for color) and optional rollups for snapshot metrics
  const changeMetricKeysCsv = useMemo(() => {
    const set = new Set<string>(['price_close']);
    const addIfMetric = (k: string, useChange: boolean) => {
      if (!useChange) return;
      if (!k || k === 'price_change') return;
      if (k.startsWith('ratio:')) return;
      set.add(k);
    };
    addIfMetric(xKey, xUseChange);
    addIfMetric(yKey, yUseChange);
    return Array.from(set).join(',');
  }, [xKey, yKey, xUseChange, yUseChange]);
  const changesQuery = useQuery({
    queryKey: ["tokens","changes","scatter", changeMetricKeysCsv, range],
    queryFn: ({ signal }) => fetchTokensChanges({ windowsCsv: range, metricKeysCsv: changeMetricKeysCsv, sortBy: "market_cap", sortOrder: "desc", limit: 2000, signal }),
    staleTime: 60_000,
  });
  const changeByTickerMetric = useMemo(() => {
    const out = new Map<string, Map<string, number>>();
    const tokens = changesQuery.data?.tokens || [];
    for (const t of tokens) {
      const inner = new Map<string, number>();
      for (const m of (t.metrics || [])) {
        const raw = m?.windows?.[range]?.value;
        const pct = typeof raw === 'number' ? (raw * 100) : 0;
        inner.set(m.metric_key, pct);
      }
      // Store by uppercase ticker for consistent lookup
      out.set(t.ticker.toUpperCase(), inner);
    }
    return out;
  }, [changesQuery.data, range]);

  const rollupMetricsCsv = useMemo(() => {
    const rollupKeys = new Set<string>(['volume', 'dex_volume', 'tvl', 'fees', 'revenue', 'futures_oi', 'futures_fr_oiw', 'futures_fr_volw']);
    if (catalog) {
      for (const m of catalog) {
        const key = (m as MetricInfo)?.metric_key;
        if (key && (key.includes('volume') || key.includes('tvl') || key.includes('fees') || key.includes('revenue') || key.includes('futures') || key.includes('oi'))) {
          rollupKeys.add(key);
        }
      }
    }
    return Array.from(rollupKeys).join(',');
  }, [catalog]);
  const rollupsQuery = useQuery({
    queryKey: ["tokens","rollups","scatter", range, rollupMetricsCsv],
    queryFn: ({ signal }) => fetchTokensRollups({ windowsCsv: range, metricKeysCsv: rollupMetricsCsv, sortBy: "market_cap", sortOrder: "desc", limit: 2000, signal }),
    staleTime: 60_000,
    enabled: rollupMetricsCsv.length > 0,
  });
  const rollByTicker = useMemo(() => {
    const out = new Map<string, Record<string, number | null>>();
    const toks = rollupsQuery.data?.tokens || [];
    for (const t of toks) {
      const rec: Record<string, number | null> = {};
      for (const m of t.metrics || []) {
        const v = (m.windows?.[range]?.value ?? null) as number | null;
        rec[m.metric_key] = v;
      }
      // Store by uppercase ticker for consistent lookup
      out.set(t.ticker.toUpperCase(), rec);
    }
    return out;
  }, [rollupsQuery.data, range]);

  const getChangePct = React.useCallback((ticker: string, metricKey: string): number => {
    const inner = changeByTickerMetric.get(ticker);
    if (!inner) return 0;
    return inner.get(metricKey) ?? 0;
  }, [changeByTickerMetric]);

  const getMetricValue = React.useCallback((ticker: string, key: string, row: Record<string, unknown>, useChange: boolean): number | null => {
    if (useChange && !key.startsWith('ratio:')) {
      return getChangePct(ticker, key);
    }
    const roll = rollByTicker.get(ticker);
    if (roll && Object.prototype.hasOwnProperty.call(roll, key)) {
      const v = roll[key];
      if (v == null || v === undefined) return null;
      const num = Number(v);
      return Number.isFinite(num) ? num : null;
    }
    const snap = row[key];
    if (snap == null || snap === undefined) return null;
    const num = Number(snap);
    return Number.isFinite(num) ? num : null;
  }, [getChangePct, rollByTicker]);

  const { option, count } = useMemo(() => {
    // tuple: [x, y, ticker, marketCap, colorVal]
    const pts: Array<[number, number, string, number, number]> = [];
    for (const r of rows as Array<Record<string, unknown>>) {
      const tickerRaw = String(r["tokenSymbol"] ?? "");
      if (!tickerRaw) continue;
      const ticker = tickerRaw.toUpperCase(); // Normalize to uppercase for consistent lookup
      // excludeStables handled upstream by filtered rows; keep as no-op here
      if (search) {
        const name = String(r["tokenName"] ?? r["name"] ?? "");
        const s = search.toLowerCase();
        if (!ticker.toLowerCase().includes(s) && !name.toLowerCase().includes(s)) continue;
      }
      const mcRaw = (r["market_cap"] as number | null) ?? (r[MetricKeys.MarketCap] as number | null) ?? null;
      const mc = mcRaw == null ? NaN : Number(mcRaw);
      if (!Number.isFinite(mc) || mc <= 0) continue;
      if (mcMin !== '' && mc < Number(mcMin)) continue;
      if (mcMax !== '' && mc > Number(mcMax)) continue;
      const xv = getMetricValue(ticker, xKey, r, xUseChange);
      const yv = yKey === MetricKeys.MarketCap && !yUseChange ? mc : getMetricValue(ticker, yKey, r, yUseChange);
      if (xv == null || !Number.isFinite(xv)) continue;
      if (yv == null || !Number.isFinite(yv)) continue;
      // If snapshot (non-change) metrics produce zeros for missing data, drop them
      if (!xUseChange && Number(xv) === 0) continue;
      if (!yUseChange && Number(yv) === 0) continue;
      const chg = getChangePct(ticker, 'price_close');
      pts.push([Number(xv), Number(yv), ticker, mc, chg]);
    }

    const xAxisName = `${labelFromCatalog(String(xKey))}${xUseChange ? ' (%)' : ''}`;
    const yAxisName = `${labelFromCatalog(String(yKey))}${yUseChange ? ' (%)' : ''}`;
    const hasNegativeX = pts.some((p) => Number.isFinite(p[0]) && p[0] < 0);
    const hasZeroOrNegX = pts.some((p) => !Number.isFinite(p[0]) || p[0] <= 0);
    const hasNegativeY = pts.some((p) => Number.isFinite(p[1]) && p[1] < 0);
    const hasZeroOrNegY = pts.some((p) => !Number.isFinite(p[1]) || p[1] <= 0);

    const mappedPts: Array<[number, number, string, number, number]> = (logX && !hasNegativeX) || (logY && !hasNegativeY)
      ? pts.map((d) => [
          (logX && !hasNegativeX && d[0] <= 0) ? 1e-12 : d[0],
          (logY && !hasNegativeY && d[1] <= 0) ? 1e-12 : d[1],
          d[2], d[3], d[4]
        ])
      : pts;

    if (typeof setLogAvailability === 'function') setLogAvailability({ x: !hasNegativeX, y: !hasNegativeY });

    const option: EChartsOption = {
      backgroundColor: 'transparent',
      textStyle: baseTextStyle,
      tooltip: {
        trigger: 'item',
        formatter: (params: TopLevelFormatterParams) => {
          const p = (Array.isArray(params) ? params[0] : params) as CallbackDataParams;
          const d = p.data as [number, number, string, number, number] | undefined;
          if (!d) return '';
          const [vx, vy, tkr, mc, ch] = d;
          const formatPercent = (val: number) => {
            const a = Math.abs(val);
            if (a < 0.01) return `${val.toFixed(4)}%`;
            if (a < 0.1) return `${val.toFixed(3)}%`;
            return `${val.toFixed(2)}%`;
          };
          const formatSmall = (val: number) => {
            const a = Math.abs(val);
            if (a === 0) return '0';
            const digits = a < 1e-6 ? 10 : a < 1e-4 ? 8 : a < 0.01 ? 6 : a < 0.1 ? 4 : 3;
            return val.toLocaleString(undefined, { minimumFractionDigits: Math.min(digits, 10), maximumFractionDigits: Math.min(digits, 10) });
          };
          const formatCurrency = (val: number) => {
            const a = Math.abs(val);
            if (a >= 1e9) return `$${(val/1e9).toFixed(2)}B`;
            if (a >= 1e6) return `$${(val/1e6).toFixed(2)}M`;
            if (a >= 1) return `$${val.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
            return `$${formatSmall(val)}`;
          };
          const formatNumber = (val: number, unit?: string) => {
            if (!unit) {
              const a = Math.abs(val);
              if (a >= 1) return val.toLocaleString(undefined, { maximumFractionDigits: 2 });
              return formatSmall(val);
            }
            const unitLower = unit.toLowerCase();
            if (unitLower.includes('usd') || unitLower.includes('$') || unitLower === 'currency') {
              return formatCurrency(val);
            }
            const a = Math.abs(val);
            const base = a >= 1 ? val.toLocaleString(undefined, { maximumFractionDigits: 2 }) : formatSmall(val);
            return `${base} ${unit}`;
          };
          const unitX = catalogMap?.[String(xKey)]?.unit as string | undefined;
          const unitY = catalogMap?.[String(yKey)]?.unit as string | undefined;
          const fmtX = xUseChange ? formatPercent(vx) : formatNumber(vx, unitX);
          const fmtY = yUseChange ? formatPercent(vy) : formatNumber(vy, unitY);
          const fmtPriceChange = formatPercent(ch);
          return `${tkr}<br/>${xAxisName}: ${fmtX}<br/>${yAxisName}: ${fmtY}<br/>MC: ${formatCurrency(mc)}<br/>Price Δ (${range}): ${fmtPriceChange}`;
        },
      },
      grid: baseGrid,
      xAxis: ((): unknown => {
        if (logX && !hasNegativeX) {
          return {
            type: 'log' as const,
            name: xAxisName,
            nameTextStyle: { color: '#cbd5e1', fontSize: 10 },
            ...axisCommon,
            axisLabel: { ...axisCommon.axisLabel, formatter: fmtAxisLabel(xKey, xUseChange) as unknown as string | ((v: number) => string) },
            min: hasZeroOrNegX ? 1e-12 : undefined,
          };
        }
        return {
          type: 'value' as const,
          name: xAxisName,
          nameTextStyle: { color: '#cbd5e1', fontSize: 10 },
          scale: true, // Auto-fit axis range to data
          ...axisCommon,
          axisLabel: { ...axisCommon.axisLabel, formatter: fmtAxisLabel(xKey, xUseChange) as unknown as string | ((v: number) => string) },
        };
      })() as NonNullable<EChartsOption['xAxis']>,
      yAxis: ((): unknown => {
        if (logY && !hasNegativeY) {
          return {
            type: 'log' as const,
            name: yAxisName,
            nameTextStyle: { color: '#cbd5e1', fontSize: 10 },
            ...axisCommon,
            axisLabel: { ...axisCommon.axisLabel, formatter: fmtAxisLabel(yKey, yUseChange) as unknown as string | ((v: number) => string) },
            min: hasZeroOrNegY ? 1e-12 : undefined,
          };
        }
        return {
          type: 'value' as const,
          name: yAxisName,
          nameTextStyle: { color: '#cbd5e1', fontSize: 10 },
          scale: true, // Auto-fit axis range to data
          ...axisCommon,
          axisLabel: { ...axisCommon.axisLabel, formatter: fmtAxisLabel(yKey, yUseChange) as unknown as string | ((v: number) => string) },
        };
      })() as NonNullable<EChartsOption['yAxis']>,
      series: [{
        type: 'scatter',
        data: mappedPts,
        symbolSize: (p: unknown) => {
          const d = p as [number, number, string, number, number];
          const mc = d?.[3] ?? 0;
          if (!Number.isFinite(mc) || mc <= 0) return 8;
          const s = Math.sqrt(mc) / Math.sqrt(1e9);
          return Math.max(8, Math.min(24, s));
        },
        itemStyle: {
          color: (p: CallbackDataParams) => {
            const d = p.data as [number, number, string, number, number] | undefined;
            const ch = d ? d[4] : 0;
            if (ch < -10) return '#a855f7';
            if (ch < 0) return '#c084fc';
            if (ch < 10) return '#60a5fa';
            return '#06b6d4';
          },
          opacity: 0.85,
        },
        emphasis: { focus: 'series', scale: 1.6 },
      }],
    };
    return { option, count: pts.length };
  }, [rows, search, mcMin, mcMax, xKey, yKey, xUseChange, yUseChange, logX, logY, range, labelFromCatalog, fmtAxisLabel,  getMetricValue, getChangePct, baseGrid, baseTextStyle, axisCommon, catalogMap, setLogAvailability]);

  const isLoading = changesQuery.isLoading || rollupsQuery.isLoading;
  const error = (changesQuery.error as Error | null | undefined)?.message || (rollupsQuery.error as Error | null | undefined)?.message || null;
  return <EChartsRenderer chartOption={option} height={height} isLoading={isLoading} error={error} hasData={count > 0} containerClassName="bg-transparent border-0 rounded-lg overflow-hidden" />;
};

export default ScatterChart;

 

